import React, { useEffect, useState, useRef, useCallback } from "react";
import { RiSearch2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { setPaginationReset } from "../../store/userSlice";
import { useSearchParams } from "react-router-dom";

const DEFAULT_LIMIT = 50;
const LIMIT_OPTIONS = [50, 100, 200, 500];

export default function useSearchPagination(
  tableData = [],
  paginationInfo = {
    page: 1,
    limit: DEFAULT_LIMIT,
    totalPages: 1,
    totalCount: 0,
  },
  fetchData,
  searchText
) {
  const dispatch = useDispatch();
  //   const [searchText, setSearchText] = useState('');
  const [searchParam] = useSearchParams();
  const { currentPagination, paginationReset } = useSelector(
    (state) => state.user
  );
  const [currentList, setCurrentList] = useState(tableData);
  const isInitialMount = useRef(true);
  const isUserInteraction = useRef(false);
  const fetchDataRef = useRef(fetchData);
  const prevPaginationInfoRef = useRef({
    page: paginationInfo.page || 1,
    limit: paginationInfo.limit || DEFAULT_LIMIT,
    totalPages: paginationInfo.totalPages || 1,
    totalCount: paginationInfo.totalCount || 0,
  });

  // Keep fetchData ref updated
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const [pagination, setPagination] = useState({
    current: paginationInfo.page || 1,
    count: paginationInfo.limit || DEFAULT_LIMIT,
    totalPages: paginationInfo.totalPages || 1,
    totalCount: paginationInfo.totalCount || tableData.length,
  });

  // Sync pagination when API data changes (but don't override user's current page selection)
  useEffect(() => {
    const currentInfo = {
      page: paginationInfo.page || 1,
      limit: paginationInfo.limit || DEFAULT_LIMIT,
      totalPages: paginationInfo.totalPages || 1,
      totalCount: paginationInfo.totalCount || 0,
    };
    
    // Only update if values actually changed
    const hasChanged = 
      prevPaginationInfoRef.current.totalPages !== currentInfo.totalPages ||
      prevPaginationInfoRef.current.totalCount !== currentInfo.totalCount ||
      prevPaginationInfoRef.current.page !== currentInfo.page ||
      prevPaginationInfoRef.current.limit !== currentInfo.limit;
    
    if (hasChanged) {
      setPagination((p) => {
        // Only update current page from API if user hasn't interacted
        const newCurrent = isUserInteraction.current ? p.current : (currentInfo.page || p.current);
        return {
          ...p,
          current: newCurrent,
          totalPages: currentInfo.totalPages || 1,
          totalCount: currentInfo.totalCount || tableData.length,
        };
      });
      setCurrentList(tableData);
      prevPaginationInfoRef.current = currentInfo;
      // Reset user interaction flag after sync
      isUserInteraction.current = false;
    } else {
      // Still update currentList if tableData changed
      setCurrentList(tableData);
    }
  }, [tableData, paginationInfo.page, paginationInfo.limit, paginationInfo.totalPages, paginationInfo.totalCount]);

  // Sync URL page param & Redux
  useEffect(() => {
    if (!paginationReset) {
      setPagination((p) => ({ ...p, current: currentPagination }));
      dispatch(setPaginationReset(true));
    }
    if (searchParam.get("page")) {
      setPagination((p) => ({ ...p, current: +searchParam.get("page") }));
    }
  }, []);

  // Call API when page, count, or searchText changes
  useEffect(() => {
    if (!fetchDataRef.current) return;
    
    // Skip the very first render to avoid duplicate initial fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (searchText && searchText.trim() !== "") {
      // Server-side search: ignore pagination
      fetchDataRef.current(undefined, undefined, searchText);
      setPagination((p) => ({ ...p, current: 1 })); // reset page number
    } else {
      // Normal paginated fetch - only if page or limit actually changed
      fetchDataRef.current(pagination.current, pagination.count, "");
    }
  }, [pagination.current, pagination.count, searchText]);

  const handleCountChange = (newCount) => {
    isUserInteraction.current = true;
    setPagination((p) => ({ ...p, count: newCount, current: 1 }));
  };

  const handlePageChange = (e) => {
    const newPage = parseInt(e?.target?.value || e, 10);
    if (!isNaN(newPage) && newPage > 0) {
      isUserInteraction.current = true;
      setPagination((p) => ({ ...p, current: newPage }));
    }
  };

  const getPageNumbers = () => {
    const total = Math.max(1, pagination.totalPages || 1);
    return Array.from({ length: total }, (_, i) => i + 1);
  };

  // // Debounced API fetch
  //   useEffect(() => {
  //     if (debounceRef.current) clearTimeout(debounceRef.current);

  //     debounceRef.current = setTimeout(() => {
  //       fetchData(1, pagination.count, searchText);
  //       setPagination((p) => ({ ...p, current: 1 }));
  //     }, 500);

  //     return () => clearTimeout(debounceRef.current);
  //   }, [pagination.count, fetchData]);

  //   // Call API when debounced search changes
  //   useEffect(() => {
  //     if (debouncedSearch !== undefined) {
  //       fetchData(1, pagination.count, debouncedSearch);
  //       setPagination((p) => ({ ...p, current: 1 }));
  //     }
  //   }, [debouncedSearch]);

  //   const handleSearch =  (value) => {
  //     // setSearchText(value);
  //     // setPagination((p) => ({ ...p, current: 1 }));
  //     // fetchData(1, pagination.count, value);
  //   }

  // const handleCountChange = (newCount) => {
  //   setPagination((p) => ({ ...p, count: newCount, current: 1 }));
  //   fetchData(1, newCount, searchText); // <--- here, newCount is sent as `limit`
  // };

  // const handlePageChange = (e) => {
  //   const newPage = parseInt(e.target.value, 10);
  //   setPagination((p) => ({ ...p, current: newPage }));
  //   fetchData(newPage, pagination.count, searchText); // call API with new page
  // };

  // Pagination UI component
  const Pagination = ({ className }) => (
    <div className={`flex flex-col items-center w-full ${className}`} style={{ position: 'relative', zIndex: 1 }}>
      <div className="flex md:gap-10 sm:gap-4 justify-center items-center bg-white p-1 text-center rounded-md mb-1 shadow-md w-full" style={{ position: 'relative', zIndex: 10 }}>
        <button
          type="button"
          disabled={pagination.current <= 1}
          className={`p-2 rounded-md ${pagination.current > 1 ? "hover:bg-blue/90 hover:text-white cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (pagination.current > 1) {
              handlePageChange({ target: { value: 1 } });
            }
          }}
        >
          {"<<"}
        </button>
        <button
          type="button"
          disabled={pagination.current <= 1}
          className={`p-2 rounded-md ${pagination.current > 1 ? "hover:bg-blue/90 hover:text-white cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (pagination.current > 1) {
              handlePageChange({ target: { value: pagination.current - 1 } });
            }
          }}
        >
          {"<"}
        </button>
        <select 
          value={pagination.current} 
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePageChange(e);
          }}
          className="cursor-pointer"
          style={{ pointerEvents: 'auto', zIndex: 10 }}
        >
          {getPageNumbers().map((pageNumber) => (
            <option key={pageNumber} value={pageNumber}>
              Page {pageNumber}
            </option>
          ))}
        </select>
        <select
          value={pagination.count}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCountChange(Number(e.target.value));
          }}
          className="cursor-pointer"
          style={{ pointerEvents: 'auto', zIndex: 10 }}
        >
          {LIMIT_OPTIONS.map((v) => (
            <option key={v} value={v}>
              {v} per page
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={pagination.current >= pagination.totalPages}
          className={`p-2 rounded-md ${pagination.current < pagination.totalPages ? "hover:bg-blue/90 hover:text-white cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (pagination.current < pagination.totalPages) {
              handlePageChange({ target: { value: pagination.current + 1 } });
            }
          }}
        >
          {">"}
        </button>
        <button
          type="button"
          disabled={pagination.current >= pagination.totalPages}
          className={`p-2 rounded-md ${pagination.current < pagination.totalPages ? "hover:bg-blue/90 hover:text-white cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (pagination.current < pagination.totalPages) {
              handlePageChange({ target: { value: pagination.totalPages } });
            }
          }}
        >
          {">>"}
        </button>
      </div>
      <p className="text-sm">
        Showing page {pagination.current} of {pagination.totalPages} (
        {pagination.totalCount} records)
      </p>
    </div>
  );

  const NoDataFound = ({ customClass, colSpan }) =>
    currentList.length <= 0 && (
      <div
        className={`flex justify-center items-center gap-3 mt-5 w-full col-span-${colSpan || 5} ${customClass || ""}`}
      >
        <p>No Data Found</p>
      </div>
    );

  const PaginationIndex = ({ index }) => (
    <p>{(pagination.current - 1) * pagination.count + index + 1}</p>
  );

  //   const CountPerPage = ({ className }) => (
  //     <div className="w-full flex justify-end">
  //       <select value={pagination.count} onChange={handleCountChange} className={`select select-sm ${className}`}>
  //         {[5, 10, 25].map((v) => <option key={v} value={v}>{v} per page</option>)}
  //       </select>
  //     </div>
  //   );

  //   const ResetFilter = () => {
  //     setFilteringKeys({});
  //     setCurrentList(tableData);
  //   };

  //   const Search = ({ placeholder = "Enter to search", className }) => (
  // <div className={`max-w-fit w-full flex items-center relative my-1 ${className}`}>
  //   <RiSearch2Line className="absolute mx-1 text-slate-400" />
  //   <input
  //     type="search"
  //     className="input input-sm h-10 max-w-xs pl-6 w-full input-bordered focus:outline-none uppercase"
  //     placeholder={placeholder}
  //     value={searchText}
  //     onChange={(e) => {
  //     const value = e.target.value;
  //     setSearchText(value)
  //     handleSearch(value);                     // for server-side API
  //     setSearchWords(value ? [value] : []);    // for highlighting
  //   }}
  //   />
  // </div>
  //   );

  return {
    Pagination,
    // Search,
    NoDataFound,
    currentList,
    paginationData: pagination,
    PaginationIndex,
  };
}
